import React from 'react';
import PropTypes from 'prop-types';

/**
 * Loading skeleton for the registration approval queue (U3.5). Mirrors the
 * table's shape — avatar + name, a couple of pills, row actions — so the
 * page doesn't jump when the data arrives. Colours come from theme variables,
 * so it follows light / dark mode on its own.
 */
const WIDTHS = [62, 48, 70, 55, 66, 44, 58, 50];

const RegistrationQueueShimmer = ({ rows = 6 }) => (
  <div className="mym-q-shimmer" aria-busy="true" aria-live="polite">
    <span className="visually-hidden">Loading registration requests…</span>
    {Array.from({ length: rows }).map((_, index) => (
      <div key={index} className="mym-q-shimmer-row">
        <span className="mym-q-shimmer-block mym-q-shimmer-circle" />
        <div className="mym-q-shimmer-stack">
          <span
            className="mym-q-shimmer-block"
            style={{ width: `${WIDTHS[index % WIDTHS.length]}%` }}
          />
          <span
            className="mym-q-shimmer-block mym-q-shimmer-thin"
            style={{ width: `${WIDTHS[(index + 3) % WIDTHS.length] - 12}%` }}
          />
        </div>
        <span className="mym-q-shimmer-block mym-q-shimmer-pill d-none d-md-inline-block" />
        <span className="mym-q-shimmer-block mym-q-shimmer-pill d-none d-lg-inline-block" />
        <span className="mym-q-shimmer-block mym-q-shimmer-pill mym-q-shimmer-pill-sm" />
        <span className="mym-q-shimmer-actions">
          <span className="mym-q-shimmer-block mym-q-shimmer-button" />
        </span>
      </div>
    ))}
    <style>
      {`
        .mym-q-shimmer { padding: 0.25rem 1rem 0.75rem; }
        .mym-q-shimmer-row {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 0.85rem 0;
          border-bottom: 1px solid var(--falcon-border-color);
        }
        .mym-q-shimmer-row:last-child { border-bottom: 0; }
        .mym-q-shimmer-stack { display: flex; flex: 1 1 auto; flex-direction: column; gap: 0.45rem; min-width: 0; }
        .mym-q-shimmer-block {
          display: inline-block;
          height: 0.8rem;
          border-radius: 0.4rem;
          background: linear-gradient(90deg, var(--falcon-gray-200) 25%, var(--falcon-gray-100) 37%, var(--falcon-gray-200) 63%);
          background-size: 400% 100%;
          animation: mymQueueShimmer 1.4s ease infinite;
        }
        .mym-q-shimmer-thin { height: 0.6rem; }
        .mym-q-shimmer-circle { flex-shrink: 0; width: 2.25rem; height: 2.25rem; border-radius: 50%; }
        .mym-q-shimmer-pill { flex-shrink: 0; width: 7rem; height: 1.4rem; border-radius: 999px; }
        .mym-q-shimmer-pill-sm { width: 4.5rem; }
        .mym-q-shimmer-actions { display: inline-flex; flex-shrink: 0; gap: 0.5rem; }
        .mym-q-shimmer-button { width: 5rem; height: 1.85rem; border-radius: 0.375rem; }
        @keyframes mymQueueShimmer {
          from { background-position: 100% 0; }
          to { background-position: 0 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .mym-q-shimmer-block { animation: none; }
        }
      `}
    </style>
  </div>
);

RegistrationQueueShimmer.propTypes = {
  rows: PropTypes.number
};

export default RegistrationQueueShimmer;
